import React, { useEffect, useState } from "react";
import {
  Form,
  AutoComplete,
  Flex,
  Input,
  DatePicker,
  Button,
  Table,
  Space,
  Col,
  Grid,
} from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import { getAccountBySeachApi } from "../../apiservice/setting"; // Adjust path as per your project
import InputNumberField from "../Input/InputNumber";

const { Summary } = Table;
const { useBreakpoint } = Grid;

const NormalPayment = ({
  onFooterChange,
  defaultAccount,
  value,
  pageType,
  conversionRate,
  accountLabel = "Received Into Account",
}) => {
  const [form] = Form.useForm();
  const screens = useBreakpoint();
  const isMobile = !screens.md || !screens.xl;

  const [data, setData] = useState([]);
  const [toAccs, setToAccs] = useState([]);
  const [selectedFromAcc, setSelectedFromAcc] = useState(null);
  
  
  useEffect(() => {
    getIntoAccs();
  }, []);

  // ✅ if parent passes `value`, sync table (but only when it changes)
  // useEffect(() => {
  //   if (!value || !Array.isArray(value)) {
  //     setData([]);
  //   } else {
  //     setData([...value]); // clone to avoid mutation risks
  //   }
  // }, [value]);

    useEffect(() => {
    if (value && Array.isArray(value)) {
      // Filter cash/bank payments (NormalPayment typically shows these)
      const normalPayments = value.filter(item => 
        item.acc_type_id === "cash_in_hand" || 
        item.acc_type_id === "bank_account" ||
        !item.acc_type_id // Some items might not have acc_type_id
      );
      setData(normalPayments);
    }
  }, [value]);

  const getIntoAccs = async () => {
    try {
      const res = await getAccountBySeachApi({
        multiType: ["cash_in_hand", "bank_account"],
      });
      setToAccs(res.data || []);
    } catch (err) {
      setToAccs([]);
      console.error(err);
    }
  };

  const handleSelectFromAccSet = (value, option) => {
    const selected = toAccs.find((acc) => acc.acc_name === value);
    setSelectedFromAcc(selected);
    form.setFieldValue("type", value);
  };

  const columns = [
    {
      title: accountLabel,
      dataIndex: "to_acc_name",
      key: "to_acc_name",
    },
    {
      title: "Amount",
      dataIndex: "tran_amount",
      key: "tran_amount",
      render: (val) => {
        const amount = Number(val);
        const finalAmount = conversionRate ? amount * conversionRate : amount;
        return amount ? finalAmount?.toFixed(2) : "0.00";
      },
    },
    { title: "Date", dataIndex: "date", key: "date" },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<EditOutlined />} onClick={() => edit(record)} />
          <Button
            icon={<DeleteOutlined />}
            onClick={() => remove(record.to_acc_id)}
          />
        </Space>
      ),
    },
  ];

  const onFinish = (values) => {
    const selectedAcc = toAccs.find((acc) => acc.acc_name === values.type);

    const newEntry = {
      to_acc_id: selectedAcc?.acc_id ?? 0,
      acc_type_id: selectedAcc?.acc_type_id ?? "",
      to_acc_name: selectedAcc?.acc_name ?? values.type,
      tran_amount: Number(values.amount),
      date: values.date ? values.date.format("YYYY-MM-DD") : null,
    };

    const newData = [...data];
    const index = newData.findIndex(
      (item) => item.to_acc_id === newEntry.to_acc_id
    );

    if (index > -1) {
      newData[index] = newEntry;
    } else {
      newData.push(newEntry);
    }

    onFooterChange(newData, "payCart");

    setData(newData);

    // calculate paidAmount here
    const totalPaid = newData.reduce(
      (sum, i) => sum + (Number(i.tran_amount) || 0),
      0
    );

    // update both payCart and paidAmount in footer
    pageType !== "salesReturn" && onFooterChange(totalPaid, "paidAmount");

    form.resetFields();
    setSelectedFromAcc(null);
  };

  const edit = (record) => {
    form.setFieldsValue({
      type: record.to_acc_name,
      amount: record.tran_amount,
      date: record.date ? moment(record.date, "YYYY-MM-DD") : null,
    });
    setSelectedFromAcc({ acc_name: record.to_acc_name });
  };

  const remove = (to_acc_id) => {
    const updatedData = data.filter((item) => item.to_acc_id !== to_acc_id);
    setData(updatedData);

    const totalPaid = updatedData.reduce(
      (sum, i) => sum + (Number(i.tran_amount) || 0),
      0
    );

    onFooterChange(updatedData, "payCart");
    onFooterChange(totalPaid, "paidAmount");
  };

  return (
    <>
      <Form
        form={form}
        onFinish={onFinish}
        layout={!isMobile ? "inline" : "horizontal"}
        // variant="filled"
        style={{
          width: "100%",
          gap: 10,
        }}
      >
        <Form.Item
          name="type"
          style={{
            flexGrow: 1,
            marginBottom: "10px",
          }}
          rules={[{ required: true, message: "Please select a type!" }]}
        >
          <AutoComplete
            options={toAccs.map((acc) => ({
              value: acc.acc_name,
              label: acc.acc_name,
              obj: acc,
              raw: acc,
            }))}
            onSelect={handleSelectFromAccSet}
            value={selectedFromAcc?.acc_name}
            data-testid="received-into-account"
            placeholder={accountLabel}
          >
            <Input />
          </AutoComplete>
        </Form.Item>

        <Form.Item
          name="amount"
          style={{
            flexGrow: 1,
            marginBottom: "10px",
          }}
          rules={[{ required: true, message: "Please input the amount!" }]}
        >
          <InputNumberField
            min={0}
            precision={2}
            placeholder="Amount"
            data-testid="amount-input"
            formatter={(value) => value} 
            parser={(value) => value}    
            onChange={(value) => {
              if (value === null || value === undefined) return;
              const amount = conversionRate ? value / conversionRate : value;
              // Do NOT format here; keep raw number
              form.setFieldsValue({ amount });
            }}
            onBlur={(e) => {
              const v = e.target.value;
              if (!v) return;
              const amount = conversionRate ? v / conversionRate : v;
              // Format properly: convert string → fixed → number again
              const formatted = Number(Number(amount).toFixed(2));
              form.setFieldsValue({ amount: formatted });
            }}
          />


        </Form.Item>

        {/* {form.getFieldValue("type") !== "CASH" && (
          <Form.Item
            name="date"
            style={{
              marginBottom: "10px",
            }}
            rules={[{ required: true, message: "Please select the date!" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        )} */}

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            data-testid="add-payment-btn"
          >
            Add
          </Button>
        </Form.Item>
      </Form>

      <Table
        columns={columns}
        dataSource={data}
        scroll={{x:"max-content"}}
        style={{ marginTop: 20, width: "100%" }}
        summary={() => (
          <Summary.Row>
            <Summary.Cell index={0} colSpan={3} className="table-footer-title">
              Total Paid:
            </Summary.Cell>
            <Summary.Cell colSpan={1} index={2} className="table-footer-data">
              {/* {data
                .reduce(
                  (sum, item) => sum + parseFloat(item.tran_amount || 0),
                  0
                )
                .toFixed(2)} */}
              {(conversionRate
                ? data.reduce(
                  (sum, item) =>
                    sum + Number(item.tran_amount || 0) * conversionRate,
                  0
                )
                : data.reduce(
                  (sum, item) => sum + Number(item.tran_amount || 0),
                  0
                )
              ).toFixed(2)}
            </Summary.Cell>
          </Summary.Row>
        )}
      />
    </>
  );
};

export default NormalPayment;
